import React, { useState, useEffect } from 'react';
import {
  Plus, Eye, Edit, Trash2,
  TrendingUp, DollarSign, MousePointer, Target, Calendar,
  Facebook, Linkedin, Chrome, Play, Settings, ExternalLink, ChevronDown, ChevronRight
} from 'lucide-react';
import axiosInstance from '../../config/axios';
import { useContentFilters } from '../../hooks/useContentFilters';
import ContentFilterBar from './ContentFilterBar';
import AdsAssetCreation from '../assets/by-type/AdsAssetCreation';

interface Ad {
  id: number;
  type: string;
  title: string;
  description?: string;
  status: 'draft' | 'active' | 'paused' | 'completed' | 'pending_review' | 'approved' | 'published' | 'rejected';
  metadata?: string; // JSON string containing platform, campaign_type, budget, etc.
  scheduled_for?: string;
  created_by?: string;
  client_id?: number;
  plan_id?: number;
  created_at: string;
  updated_at?: string;
  // Parsed from metadata
  platform?: string;
  campaign_type?: string;
  budget?: string;
  spend?: number;
  impressions?: number;
  clicks?: number;
  conversions?: number;
}

export default function AdsManagement() {
  const [ads, setAds] = useState<Ad[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Use content filters hook
  const {
    filters,
    grouping,
    filteredItems,
    groupedItems,
    activeFilterCount,
    updateFilter,
    clearFilters,
    removeFilter,
    setGrouping,
    isGrouped,
    hasSecondaryGrouping,
  } = useContentFilters(ads, 'ads');

  const toggleGroup = (groupKey: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupKey)) {
      newExpanded.delete(groupKey);
    } else {
      newExpanded.add(groupKey);
    }
    setExpandedGroups(newExpanded);
  };

  const expandAll = () => {
    if (hasSecondaryGrouping) {
      const allKeys = new Set<string>();
      Object.keys(groupedItems).forEach(primaryKey => {
        allKeys.add(primaryKey);
        Object.keys((groupedItems as any)[primaryKey]).forEach((secondaryKey: string) => {
          allKeys.add(`${primaryKey}::${secondaryKey}`);
        });
      });
      setExpandedGroups(allKeys);
    } else {
      setExpandedGroups(new Set(Object.keys(groupedItems)));
    }
  };

  const collapseAll = () => {
    setExpandedGroups(new Set());
  };

  useEffect(() => {
    loadAds();
  }, []);

  const loadAds = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/content?type=ads');
      if (response.data.items) {
        // Parse metadata for each ad
        const parsedAds = response.data.items.map((ad: any) => {
          let metadata = {};
          try {
            metadata = ad.metadata ? JSON.parse(ad.metadata) : {};
          } catch (e) {
            console.warn('Failed to parse metadata for ad:', ad.id);
          }
          return {
            ...ad,
            platform: metadata.platform || 'unknown',
            campaign_type: metadata.campaign_type || 'unknown',
            budget: metadata.budget || '0',
            ...metadata
          };
        });
        setAds(parsedAds);
      }
    } catch (error) {
      console.error('Failed to load ads:', error);
      setAds([]); // Empty array on error - no mock data
    } finally {
      setLoading(false);
    }
  };

  const getPlatformIcon = (platform?: string) => {
    if (!platform) return <Target className="h-5 w-5 text-gray-600" />;

    switch (platform.toLowerCase()) {
      case 'google':
      case 'google_ads':
        return <Chrome className="h-5 w-5 text-blue-600" />;
      case 'facebook':
      case 'facebook_ads':
      case 'instagram':
        return <Facebook className="h-5 w-5 text-blue-500" />;
      case 'linkedin':
      case 'linkedin_ads':
        return <Linkedin className="h-5 w-5 text-blue-700" />;
      case 'youtube':
        return <Play className="h-5 w-5 text-red-600" />;
      case 'display':
        return <Target className="h-5 w-5 text-purple-600" />;
      default:
        return <Target className="h-5 w-5 text-gray-600" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      paused: 'bg-yellow-100 text-yellow-800',
      draft: 'bg-gray-100 text-gray-800',
      completed: 'bg-blue-100 text-blue-800',
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status as keyof typeof styles] || styles.draft}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // Mock clients and projects for filters
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Ads Management</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage ad campaigns across all platforms
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
        >
          <Plus className="h-5 w-5 mr-2" />
          Create Campaign
        </button>
      </div>

      {/* Filters */}
      <ContentFilterBar
        contentType="ads"
        filters={filters}
        grouping={grouping}
        activeFilterCount={activeFilterCount}
        onFilterChange={updateFilter}
        onClearFilters={clearFilters}
        onRemoveFilter={removeFilter}
        onGroupingChange={(changes) => setGrouping({ ...grouping, ...changes })}
        clients={[]}
        projects={[]}
        phases={[]}
      />

      {/* Campaigns Table */}
      {!isGrouped ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Campaign
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client / Project
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Platform
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Budget
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Scheduled
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredItems.map((ad) => (
                <tr key={ad.id} className="hover:bg-gray-50 transition">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        {getPlatformIcon(ad.platform)}
                      </div>
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900">{ad.title}</div>
                        <div className="text-xs text-gray-500">{ad.campaign_type || 'Campaign'}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">{ad.client_name || `Client #${ad.client_id}`}</div>
                    <div className="text-xs text-gray-500">{ad.project_name || 'No project'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{ad.platform || 'Unknown'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(ad.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{ad.budget || 'N/A'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {ad.scheduled_for ? new Date(ad.scheduled_for).toLocaleDateString() : 'Not scheduled'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded">
                        <Eye className="h-4 w-4" />
                      </button>
                      <button className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded">
                        <Edit className="h-4 w-4" />
                      </button>
                      <button className="text-gray-600 hover:text-red-600 p-1 hover:bg-red-50 rounded">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              </tbody>
            </table>
          </div>

          {/* Empty State */}
          {filteredItems.length === 0 && (
            <div className="text-center py-12">
              <Target className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No campaigns found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {activeFilterCount > 0
                  ? 'Try adjusting your filters'
                  : 'Get started by creating your first ad campaign'}
              </p>
              {activeFilterCount === 0 && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="mt-4 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  Create Campaign
                </button>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {/* Expand/Collapse All Controls */}
          {isGrouped && Object.keys(groupedItems).length > 0 && (
            <div className="flex items-center gap-2 bg-white rounded-lg shadow-sm border border-gray-200 p-3">
              <span className="text-sm font-medium text-gray-700">Groups:</span>
              <button
                onClick={expandAll}
                className="px-3 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded transition"
              >
                Expand All
              </button>
              <button
                onClick={collapseAll}
                className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded transition"
              >
                Collapse All
              </button>
            </div>
          )}

          {/* Single-Level Grouping */}
          {!hasSecondaryGrouping && Object.entries(groupedItems).map(([groupName, groupAds]) => {
            const isExpanded = expandedGroups.has(groupName);
            return (
            <div key={groupName} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <button
                onClick={() => toggleGroup(groupName)}
                className="w-full bg-gray-50 px-6 py-3 border-b border-gray-200 flex items-center justify-between hover:bg-gray-100 transition"
              >
                <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2">
                  {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  {groupName} ({Array.isArray(groupAds) ? groupAds.length : 0})
                </h3>
              </button>
              {isExpanded && (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaign</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Platform</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Budget</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Scheduled</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {groupAds.map((ad: Ad) => (
                      <tr key={ad.id} className="hover:bg-gray-50 transition">
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className="flex-shrink-0">{getPlatformIcon(ad.platform)}</div>
                            <div className="ml-3">
                              <div className="text-sm font-medium text-gray-900">{ad.title}</div>
                              <div className="text-xs text-gray-500">{ad.campaign_type || 'Campaign'}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{ad.platform || 'Unknown'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(ad.status)}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{ad.budget || 'N/A'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {ad.scheduled_for ? new Date(ad.scheduled_for).toLocaleDateString() : 'Not scheduled'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <button className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded"><Eye className="h-4 w-4" /></button>
                            <button className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded"><Edit className="h-4 w-4" /></button>
                            <button className="text-gray-600 hover:text-red-600 p-1 hover:bg-red-50 rounded"><Trash2 className="h-4 w-4" /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              )}
            </div>
          );
          })}

          {/* Two-Level Grouping (Primary -> Secondary) */}
          {hasSecondaryGrouping && Object.entries(groupedItems).map(([primaryKey, secondaryGroups]) => {
            const isPrimaryExpanded = expandedGroups.has(primaryKey);
            const totalCount = Object.values(secondaryGroups as Record<string, Ad[]>).reduce((sum, arr) => sum + arr.length, 0);

            return (
              <div key={primaryKey} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                {/* Primary Group Header */}
                <button
                  onClick={() => toggleGroup(primaryKey)}
                  className="w-full bg-blue-50 px-6 py-3 border-b border-gray-200 flex items-center justify-between hover:bg-blue-100 transition"
                >
                  <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                    {isPrimaryExpanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                    {primaryKey} ({totalCount})
                  </h3>
                </button>

                {/* Secondary Groups */}
                {isPrimaryExpanded && (
                  <div className="pl-6">
                    {Object.entries(secondaryGroups as Record<string, Ad[]>).map(([secondaryKey, items]) => {
                      const secondaryGroupKey = `${primaryKey}::${secondaryKey}`;
                      const isSecondaryExpanded = expandedGroups.has(secondaryGroupKey);

                      return (
                        <div key={secondaryGroupKey} className="border-b border-gray-100 last:border-b-0">
                          <button
                            onClick={() => toggleGroup(secondaryGroupKey)}
                            className="w-full bg-gray-50 px-6 py-2.5 flex items-center justify-between hover:bg-gray-100 transition"
                          >
                            <h4 className="text-sm font-medium text-gray-800 flex items-center gap-2">
                              {isSecondaryExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                              {secondaryKey} ({items.length})
                            </h4>
                          </button>

                          {isSecondaryExpanded && (
                            <div className="overflow-x-auto">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Campaign</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Platform</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Budget</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Scheduled</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {items.map((ad: Ad) => (
                                    <tr key={ad.id} className="hover:bg-gray-50 transition">
                                      <td className="px-6 py-4">
                                        <div className="flex items-center">
                                          <div className="flex-shrink-0">{getPlatformIcon(ad.platform)}</div>
                                          <div className="ml-3">
                                            <div className="text-sm font-medium text-gray-900">{ad.title}</div>
                                            <div className="text-xs text-gray-500">{ad.campaign_type || 'Campaign'}</div>
                                          </div>
                                        </div>
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">{ad.platform || 'Unknown'}</div>
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(ad.status)}</td>
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">{ad.budget || 'N/A'}</div>
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">
                                          {ad.scheduled_for ? new Date(ad.scheduled_for).toLocaleDateString() : 'Not scheduled'}
                                        </div>
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <div className="flex items-center space-x-2">
                                          <button className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded"><Eye className="h-4 w-4" /></button>
                                          <button className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded"><Edit className="h-4 w-4" /></button>
                                          <button className="text-gray-600 hover:text-red-600 p-1 hover:bg-red-50 rounded"><Trash2 className="h-4 w-4" /></button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create Campaign Modal - Full Screen */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-white z-50 overflow-auto">
          <div className="min-h-screen">
            {/* Header with Close Button */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-xl font-semibold text-gray-900">Create Ad Campaign</h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  loadAds(); // Refresh data after creation
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              >
                Close
              </button>
            </div>

            {/* Render Ads Asset Creation Component */}
            <div className="p-6">
              <AdsAssetCreation />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
